import { StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { OrmService } from '../../schema/orm.service';
import { UserFirestoreService } from '../../services/user.firestore.service';
import { UserActions } from './user.action';
import { UserStateModel } from './user.store';
import { patchState } from '../../nested-value-getter';
@StateRepository()
@State<UserStateModel>({
  name: 'users',
  defaults: {
    users: [],
    isSubscribed: false,
    isLoading: false,
    filter: undefined,
  },
})
@Injectable()
export class UserState extends NgxsDataRepository<UserStateModel> {
  private dbUser = inject(OrmService).userCollection;
  private userService = inject(UserFirestoreService);
  @Action(UserActions.GetAllUser)
  async getAllUsers(ctx: StateContext<UserStateModel>) {
    ctx.patchState({ isLoading: true });
    const users = await this.userService.GetAll();
    ctx.patchState({
      users: users.map(({ data, ref }) => ({ ...data, ref })),
      isLoading: false,
    });
  }
  @Action(UserActions.InitUsers)
  initUsers(ctx: StateContext<UserStateModel>, { id }: UserActions.InitUsers) {
    const state = ctx.getState();
    ctx.patchState({ isLoading: true });
    if (!state.isSubscribed) {
      ctx.patchState({ isSubscribed: true });
      let off;
      if (id) {
        off = this.userService.OnGet(this.dbUser.ref(this.dbUser.id(id)).id, (data) =>
          ctx.dispatch(new UserActions.UpdateUserResult([data])),
        );
      } else {
        off = this.userService.OnGetAll((data) => ctx.dispatch(new UserActions.UpdateUserResult(data)));
      }
      off.catch((err) => ctx.dispatch(new UserActions.UserError(err as Error)));
      ctx.patchState({ off });
      ctx.patchState({ isLoading: 'end' });
    }
  }

  @Action(UserActions.CloseUsersConnection)
  closeUsersConnection(ctx: StateContext<UserStateModel>) {
    const state = ctx.getState();
    if (state.isSubscribed && state.off) {
      state.off();
    }
    ctx.patchState({ isSubscribed: false });
  }

  @Action(UserActions.UpdateUser)
  async updateMachine(ctx: StateContext<UserStateModel>, { userData, ref }: UserActions.UpdateUser) {
    await this.userService.Update(ref, userData);
  }

  @Action(UserActions.UserError)
  UserError(ctx: StateContext<UserStateModel>, { error }: UserActions.UserError) {
    console.error({ error });
  }

  /**
   * Mirrors `MachineState.deleteMachines`: the list is filtered by the ids Firestore actually
   * confirmed, never by the ids that were asked for, so a partial failure leaves the rows that
   * still exist on screen instead of hiding them.
   */
  @Action(UserActions.DeleteUser)
  async deleteUsers(ctx: StateContext<UserStateModel>, { refs }: UserActions.DeleteUser) {
    const users = ctx.getState().users;
    const deleteRes = await this.userService.BatchDelete(refs);
    if (deleteRes) {
      const deletedIds = deleteRes.map((deletedRef) => deletedRef.id);
      ctx.patchState({ users: users.filter((user) => !deletedIds.includes(user.ref.id)) });
    }
  }

  @Action(UserActions.QueryUser)
  async queryUser(ctx: StateContext<UserStateModel>, { filter }: UserActions.QueryUser) {
    ctx.patchState({ isLoading: true });
    const users = await this.userService.QueryAll(filter.query);
    ctx.patchState({
      users: users.map(({ data, ref }) => ({ ...data, ref })),
      isLoading: false,
    });
  }

  @Action(UserActions.UpdateUserResult)
  updateUserResult(ctx: StateContext<UserStateModel>, { data }: UserActions.UpdateUserResult) {
    patchState(ctx, 'users', data);
  }

  @Selector()
  static users(state: UserStateModel) {
    return state.users;
  }

  @Selector()
  static userState(state: UserStateModel) {
    return state;
  }
}
